import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyledCheckAllWrapper, StyledRotatingIconButton } from '../../styles';
import {
  StyledAutomationFolderChildren,
  StyledAutomationFolderChildrenInner,
  StyledAutomationCheckbox,
  StyledAutomationFolderCheckbox,
  StyledCounts,
} from './styles';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { colorsButton } from 'app/colors';
import Checkbox from '__components/Inputs/CheckboxGroup/Checkbox';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import {
  getAllAutomationsInNode,
  getAllFolderIdsInNode,
  type AutomationNode,
  type AutomationOption,
} from './helpers';

export const AutomationFolderNode: FC<{
  node: AutomationNode;
  depth: number;
  selectedAutomations: Set<string>;
  selectedFolderIds: Set<string>;
  setSelectedAutomations: Dispatch<SetStateAction<AutomationOption[]>>;
  setSelectedFolders: Dispatch<SetStateAction<string[]>>;
}> = ({
  node,
  depth,
  selectedAutomations,
  selectedFolderIds,
  setSelectedAutomations,
  setSelectedFolders,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const allAutomations = useMemo(() => getAllAutomationsInNode(node), [node]);
  const allFolderIds = useMemo(() => getAllFolderIdsInNode(node), [node]);

  const folderChecked = selectedFolderIds.has(node.id);
  const selectedCountInFolder = allAutomations.filter((a) =>
    selectedAutomations.has(a.value)
  ).length;
  const someSelected = !folderChecked && selectedCountInFolder > 0;

  const handleFolderChange = (checked: boolean) => {
    const autoIds = new Set(allAutomations.map((a) => a.value));
    const folderIds = new Set(allFolderIds);

    if (checked) {
      setSelectedAutomations((prev) => {
        const existingIds = new Set(prev.map((a) => a.value));
        return [
          ...prev,
          ...allAutomations.filter((a) => !existingIds.has(a.value)),
        ];
      });
      setSelectedFolders((prev) => {
        const existingIds = new Set(prev);
        return [...prev, ...allFolderIds.filter((id) => !existingIds.has(id))];
      });
    } else {
      setSelectedAutomations((prev) =>
        prev.filter((a) => !autoIds.has(a.value))
      );
      setSelectedFolders((prev) => prev.filter((id) => !folderIds.has(id)));
    }
  };

  const handleAutomationChange = (
    automation: AutomationOption,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedAutomations((prev) => {
        if (prev.some((a) => a.value === automation.value)) return prev;
        return [...prev, automation];
      });
    } else {
      setSelectedAutomations((prev) =>
        prev.filter((a) => a.value !== automation.value)
      );
    }
  };

  return (
    <>
      <StyledCheckAllWrapper
        data-qa-label={`folder-${node.name}`}
        style={{
          paddingLeft: `${depth * 20}px`,
          height: '27px',
          marginBottom: '4px',
        }}
      >
        <StyledAutomationFolderCheckbox
          checked={folderChecked}
          indeterminate={someSelected}
          label={
            <>
              <TextEllipsisWithTooltip as="span">
                {node.name}
              </TextEllipsisWithTooltip>
              <StyledCounts as="span">
                ({selectedCountInFolder} / {allAutomations.length})
              </StyledCounts>
            </>
          }
          onChange={handleFolderChange}
          data-qa={`automation-folder-checkbox-${node.id}`}
        />
        <StyledRotatingIconButton
          sizing="dense"
          color={colorsButton.iconGray}
          onClick={() => setExpanded(!expanded)}
          expanded={expanded}
          title={expanded ? t('Collapse') : t('Expand')}
        >
          <IconSizing size="14px">
            <Icon icon="chevron-down" />
          </IconSizing>
        </StyledRotatingIconButton>
      </StyledCheckAllWrapper>

      <StyledAutomationFolderChildren expanded={expanded}>
        <StyledAutomationFolderChildrenInner>
          {node.children.map((child) => (
            <AutomationFolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedAutomations={selectedAutomations}
              selectedFolderIds={selectedFolderIds}
              setSelectedAutomations={setSelectedAutomations}
              setSelectedFolders={setSelectedFolders}
            />
          ))}
          {node.automations.map((automation) => (
            <StyledAutomationCheckbox
              key={automation.value}
              style={{ paddingLeft: (depth + 1) * 20 }}
            >
              <Checkbox
                checked={selectedAutomations.has(automation.value)}
                label={automation.label}
                onChange={(checked: boolean) =>
                  handleAutomationChange(automation, checked)
                }
                data-qa={`automation-checkbox-${automation.value}`}
              />
            </StyledAutomationCheckbox>
          ))}
        </StyledAutomationFolderChildrenInner>
      </StyledAutomationFolderChildren>
    </>
  );
};
